import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { authApi } from "@/lib/api/auth";
import { useBranding } from "@/lib/branding";
import { branchesApi } from "@/lib/api/branches";
import { qk } from "@/lib/query-client";
import { notify } from "@/lib/notify";
import { useAuthStore } from "@/stores/auth-store";
import { Card, CardBody } from "@/components/ui/Card";
import { StatusBadge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Icon } from "@/components/layout/Icon";

/** Multi-Branch Tenant Admin: the deliberate workspace-selection step a
 * Tenant Admin with 2+ active branches lands on before the ordinary
 * workspace (Sidebar/Topbar/AppShell) ever renders - see
 * RequireActiveBranch.tsx, which is what routes here and is the only thing
 * that reads the choice this page writes (setActiveBranch). Reuses
 * branchesApi.list exactly as the existing Branch Management module does -
 * one call already returns everything a card needs (name/code/location/
 * manager/employee count/status), no new backend endpoint. */
export function BranchSelectionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const setActiveBranch = useAuthStore((s) => s.setActiveBranch);
  const signOut = useAuthStore((s) => s.signOut);
  const branding = useBranding();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: qk.branches.list({ status: "active", per_page: 100 }),
    queryFn: () => branchesApi.list({ status: "active", per_page: 100 }),
  });
  const branches = data?.items ?? [];

  const logoutMutation = useMutation({
    meta: { silent: true },
    mutationFn: authApi.logout,
    onSettled: () => {
      signOut();
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });

  function selectBranch(branchId: string) {
    const branch = branches.find((b) => b.id === branchId);
    if (!branch) return;
    setActiveBranch(branch);
    notify.success(`Switched to ${branch.name}`);
    navigate("/", { replace: true });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-brand px-4 py-6 sm:px-8">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={branding.logoSrc} alt={`${branding.companyName} logo`} className="h-10 w-10 shrink-0 rounded-xl object-contain" />
            <div>
              <p className="text-sm font-medium text-primary-100">{branding.companyName}</p>
              <h1 className="text-lg font-semibold text-white">Select Branch</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 text-right">
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-white">{user?.full_name}</p>
              <p className="text-xs text-primary-200">Tenant Admin</p>
            </div>
            <button
              onClick={() => logoutMutation.mutate()}
              className="rounded-lg border border-white/20 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/10"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-8">
        <p className="mb-6 text-sm text-slate-500">
          Your organization has multiple branches. Choose one to continue - you can switch branches at any time from the header.
        </p>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardBody className="space-y-3">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-3 w-1/3" />
                  <Skeleton className="h-8 w-28" />
                </CardBody>
              </Card>
            ))}
          </div>
        ) : isError ? (
          <EmptyState
            variant="error"
            icon={<Icon name="alert-triangle" className="h-7 w-7" />}
            title="Unable to load branches"
            description="Please try again."
            action={
              <button
                onClick={() => refetch()}
                className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
              >
                Retry
              </button>
            }
          />
        ) : branches.length === 0 ? (
          <EmptyState
            icon={<Icon name="building" className="h-7 w-7" />}
            title="No active branches yet"
            description="Create one from Branch Management to continue, or contact your tenant administrator to get set up."
            action={
              <button
                onClick={() => navigate("/branches")}
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-gradient-brand px-4 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90"
              >
                <Icon name="plus" className="h-4 w-4" />
                Go to Branch Management
              </button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {branches.map((branch) => (
              <Card key={branch.id} interactive className="flex flex-col">
                <CardBody className="flex flex-1 flex-col gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                        <Icon name="building" className="h-4.5 w-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-display text-base font-medium text-slate-900">{branch.name}</p>
                        <p className="text-xs text-slate-500">Code: {branch.code}</p>
                      </div>
                    </div>
                    <StatusBadge status={branch.status} />
                  </div>

                  <div className="flex-1 space-y-1.5 text-sm text-slate-600">
                    {(branch.city || branch.state) && (
                      <p className="flex items-center gap-1.5">
                        <Icon name="map-pin" className="h-3.5 w-3.5 text-slate-400" />
                        {[branch.city, branch.state].filter(Boolean).join(", ")}
                      </p>
                    )}
                    <p className="flex items-center gap-1.5">
                      <Icon name="users" className="h-3.5 w-3.5 text-slate-400" />
                      Manager: {branch.branch_head?.full_name ?? "Unassigned"}
                    </p>
                    <p className="flex items-center gap-1.5">
                      <Icon name="briefcase" className="h-3.5 w-3.5 text-slate-400" />
                      {branch.employee_count ?? 0} employee{branch.employee_count === 1 ? "" : "s"}
                    </p>
                  </div>

                  <button
                    onClick={() => selectBranch(branch.id)}
                    className="mt-1 inline-flex h-9 w-full items-center justify-center rounded-lg bg-gradient-brand text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90"
                  >
                    Select Branch
                  </button>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
